import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, List, Popconfirm, Select, Space, Tag, Typography } from 'antd';
import {
  integrationsApi,
  type IntegrationProvider,
  type IntegrationStatus,
} from '../integrations.api';
import { MetaInsightsPanel } from './MetaInsightsPanel';

const LABELS: Record<IntegrationProvider, string> = {
  meta: 'Meta (Facebook + Instagram)',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  linkedin: 'LinkedIn',
  pinterest: 'Pinterest',
};

/**
 * Lets an admin choose which Facebook Page this project publishes to (and shows
 * whether an Instagram account is linked). Without a selection the server falls
 * back to the first administered Page, so we nudge the user to pick explicitly.
 */
function MetaPagePicker({
  orgId,
  projectId,
  status,
  canEdit,
  integrationsKey,
}: {
  orgId: string;
  projectId: string;
  status: IntegrationStatus;
  canEdit: boolean;
  integrationsKey: readonly unknown[];
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const pagesQuery = useQuery({
    queryKey: ['meta-pages', orgId, projectId],
    queryFn: () => integrationsApi.listMetaPages(orgId, projectId),
    enabled: canEdit,
  });

  const selectMutation = useMutation({
    mutationFn: (pageId: string) => integrationsApi.selectMetaPage(orgId, projectId, pageId),
    onSuccess: (sel) => {
      void queryClient.invalidateQueries({ queryKey: integrationsKey });
      message.success(`Publishing to ${sel.pageName}`);
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Could not set the page'),
  });

  if (!canEdit) {
    return (
      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
        Publishing page: {status.pageName ?? 'not selected yet'}
        {status.instagramLinked ? ' · Instagram linked' : ''}
      </Typography.Text>
    );
  }

  return (
    <Space wrap size={8} align="center" style={{ marginTop: 4 }}>
      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
        Publishing page:
      </Typography.Text>
      <Select<string>
        size="small"
        style={{ minWidth: 240 }}
        placeholder="Select a Facebook Page"
        value={status.pageId}
        loading={pagesQuery.isLoading}
        disabled={selectMutation.isPending}
        onChange={(v) => selectMutation.mutate(v)}
        options={(pagesQuery.data ?? []).map((p) => ({
          value: p.id,
          label: p.instagramLinked ? `${p.name} · IG linked` : p.name,
        }))}
        notFoundContent={pagesQuery.isLoading ? 'Loading…' : 'No Pages found on this account'}
      />
      {status.instagramLinked ? <Tag color="magenta">IG linked</Tag> : null}
      {!status.pageId ? <Tag color="warning">No page selected</Tag> : null}
    </Space>
  );
}

/**
 * Lets an admin choose which ad account this project reads insights from and
 * launches ads on. Without a selection the server picks the account with the
 * most spend, but multi-account users should pick explicitly.
 */
function MetaAdAccountPicker({
  orgId,
  projectId,
  status,
  canEdit,
  integrationsKey,
}: {
  orgId: string;
  projectId: string;
  status: IntegrationStatus;
  canEdit: boolean;
  integrationsKey: readonly unknown[];
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const accountsQuery = useQuery({
    queryKey: ['meta-adaccounts', orgId, projectId],
    queryFn: () => integrationsApi.listMetaAdAccounts(orgId, projectId),
    enabled: canEdit,
  });

  const selectMutation = useMutation({
    mutationFn: (adAccountId: string) =>
      integrationsApi.selectMetaAdAccount(orgId, projectId, adAccountId),
    onSuccess: (sel) => {
      void queryClient.invalidateQueries({ queryKey: integrationsKey });
      void queryClient.invalidateQueries({ queryKey: ['meta-insights', orgId, projectId] });
      message.success(`Ads account: ${sel.adAccountName}`);
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Could not set the ad account'),
  });

  if (!canEdit) {
    return (
      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
        Ads account: {status.adAccountName ?? 'auto (highest spend)'}
      </Typography.Text>
    );
  }

  return (
    <Space wrap size={8} align="center" style={{ marginTop: 4 }}>
      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
        Ads account:
      </Typography.Text>
      <Select<string>
        size="small"
        style={{ minWidth: 240 }}
        placeholder="Auto (account with most spend)"
        value={status.adAccountId}
        loading={accountsQuery.isLoading}
        disabled={selectMutation.isPending}
        onChange={(v) => selectMutation.mutate(v)}
        options={(accountsQuery.data ?? []).map((a) => ({
          value: a.id,
          label: `${a.name} · ${a.currency}`,
        }))}
        notFoundContent={accountsQuery.isLoading ? 'Loading…' : 'No ad accounts found'}
      />
    </Space>
  );
}

export function IntegrationsTab({
  orgId,
  projectId,
  canEdit,
}: {
  orgId: string;
  projectId: string;
  canEdit: boolean;
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const key = ['integrations', orgId, projectId] as const;

  const query = useQuery({ queryKey: key, queryFn: () => integrationsApi.list(orgId, projectId) });

  const connectMutation = useMutation({
    mutationFn: (provider: IntegrationProvider) =>
      integrationsApi.connect(orgId, projectId, provider),
    onSuccess: ({ authUrl }) => {
      // Hand off to the provider's consent screen.
      window.location.assign(authUrl);
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Could not start connection'),
  });

  const disconnectMutation = useMutation({
    mutationFn: (provider: IntegrationProvider) =>
      integrationsApi.disconnect(orgId, projectId, provider),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: key });
      message.success('Disconnected');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Could not disconnect'),
  });

  const metaConnected = (query.data ?? []).some((i) => i.provider === 'meta' && i.connected);

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <List<IntegrationStatus>
        loading={query.isLoading}
        dataSource={query.data ?? []}
        renderItem={(it) => (
          <List.Item
            actions={[
              it.connected ? (
                canEdit ? (
                  <Popconfirm
                    key="disc"
                    title={`Disconnect ${LABELS[it.provider]}?`}
                    onConfirm={() => disconnectMutation.mutate(it.provider)}
                  >
                    <Button size="small" danger>
                      Disconnect
                    </Button>
                  </Popconfirm>
                ) : (
                  <span key="c" />
                )
              ) : (
                <Button
                  key="conn"
                  size="small"
                  type="primary"
                  disabled={!canEdit || !it.configured}
                  loading={connectMutation.isPending && connectMutation.variables === it.provider}
                  onClick={() => connectMutation.mutate(it.provider)}
                >
                  Connect
                </Button>
              ),
            ]}
          >
            <List.Item.Meta
              title={
                <Space>
                  {LABELS[it.provider]}
                  {it.connected ? (
                    <Tag color="green">Connected</Tag>
                  ) : it.configured ? (
                    <Tag>Not connected</Tag>
                  ) : (
                    <Tag color="default">Not configured</Tag>
                  )}
                </Space>
              }
              description={
                it.connected ? (
                  <Space direction="vertical" size={2}>
                    {it.accountName ? <span>Account: {it.accountName}</span> : null}
                    {it.provider === 'meta' ? (
                      <>
                        <MetaPagePicker
                          orgId={orgId}
                          projectId={projectId}
                          status={it}
                          canEdit={canEdit}
                          integrationsKey={key}
                        />
                        <MetaAdAccountPicker
                          orgId={orgId}
                          projectId={projectId}
                          status={it}
                          canEdit={canEdit}
                          integrationsKey={key}
                        />
                      </>
                    ) : null}
                  </Space>
                ) : it.configured ? (
                  'Click Connect to authorize your account'
                ) : (
                  'Not yet enabled — your platform admin must configure this provider once'
                )
              }
            />
          </List.Item>
        )}
      />
      {metaConnected ? <MetaInsightsPanel orgId={orgId} projectId={projectId} /> : null}
    </Space>
  );
}
